/**
 * Auth service — login, logout, and the stored session token.
 *
 * The backend protects every /api/db, /api/dashboard and /api/sync endpoint
 * (see backend/src/server.js), so the token produced here is what makes the
 * entire dashboard work. It is attached to outgoing requests by the shared
 * interceptors in services/dashboardAPI.js.
 *
 * WHERE THE TOKEN IS STORED — and the tradeoff:
 *   localStorage is readable by any JavaScript running on the page, so a
 *   cross-site-scripting bug would expose the token. The more secure
 *   alternative is an httpOnly cookie, which JavaScript cannot read at all —
 *   but that requires cookie-based CORS plus CSRF protection on every
 *   state-changing endpoint, which is a larger change than this single-client
 *   deployment needs today. localStorage is paired here with a short token TTL
 *   (JWT_EXPIRES_IN, default 12h) to bound the damage. If this app ever starts
 *   rendering user-supplied HTML, revisit this choice first.
 */

import axios from 'axios';
import { API_ROOT, REQUEST_TIMEOUT } from '../config';

const TOKEN_KEY = 'tally_auth_token';
const USER_KEY = 'tally_auth_user';

/** Broadcast when the server rejects our token, so App can return to login. */
export const UNAUTHORIZED_EVENT = 'tally:unauthorized';

const authClient = axios.create({
  baseURL: `${API_ROOT}/auth`,
  headers: { 'Content-Type': 'application/json' },
  timeout: REQUEST_TIMEOUT,
});

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private-browsing modes and some embedded webviews throw on localStorage
    // access rather than returning null. Treat that as "not logged in" instead
    // of crashing the whole app on first render.
    return null;
  }
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeSession(token, user) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* storage unavailable — session simply won't survive a reload */
  }
}

export function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Called by the API interceptors when a request comes back 401. */
export function notifyUnauthorized() {
  clearSession();
  window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
}

/**
 * Exchanges credentials for a token. Throws an Error whose message is safe to
 * show directly in the login form.
 */
export async function login(username, password) {
  try {
    const { data } = await authClient.post('/login', { username, password });
    if (!data?.data?.token) throw new Error('Login failed — unexpected response from server');
    storeSession(data.data.token, data.data.user);
    return data.data.user;
  } catch (error) {
    // The backend returns a deliberately generic message for bad credentials
    // and a specific one for throttling; surface whichever it sent.
    const serverMessage = error.response?.data?.error;
    if (serverMessage) throw new Error(serverMessage);
    if (error.code === 'ECONNABORTED') throw new Error('Server took too long to respond. Try again.');
    throw new Error('Cannot reach the server. Check your connection and try again.');
  }
}

/**
 * Verifies a token already in storage. Called once on app start so an expired
 * session sends the user to the login screen immediately, rather than letting
 * the dashboard render and then fail request by request.
 */
export async function verifySession() {
  const token = getToken();
  if (!token) return null;

  try {
    const { data } = await authClient.get('/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data?.data || null;
  } catch (error) {
    if (error.response?.status === 401) {
      clearSession();
      return null;
    }
    // Network/server error rather than a rejected token: keep the stored
    // session and let the user retry, instead of logging them out because the
    // backend happened to be restarting.
    throw new Error('Cannot reach the server.');
  }
}

export function logout() {
  clearSession();
  window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
}
